import * as React from 'react';

import { SearchField, Toggle, ToggleButtonGroup } from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import * as state from '../../_state/loadList.state';

import { useDebouncedAtom } from '~/shared/hooks/useDebouncedAtom';

export type LoadStatusToggleProps = {
  id: string;
  count?: number;
  label: string;
  disabled?: boolean;
};

const LoadStatusToggle = ({
  id,
  count = 0,
  label,
  disabled,
}: LoadStatusToggleProps) => (
  <Toggle id={id} isDisabled={disabled} className="@LoadStatusToggle">
    <div className="flex items-center gap-1.5">
      <span>{label}</span>
      {!!count && (
        <span className="rounded-md bg-neutral/10 px-1 text-xs">{count}</span>
      )}
    </div>
  </Toggle>
);

export const LoadListFilterBar = () => {
  const [statusFilter, setStatusFilter] = useAtom(state.$loadStatusFilter);
  const [search, setSearch] = useDebouncedAtom(state.$searchParam, 300);
  const setOffset = useSetAtom(state.$offset);

  const { data: loadsCountingByStatus } = useAtomValue(
    state.$loadsCountingByStatus,
  );

  const allCount =
    (loadsCountingByStatus?.shipped ?? 0) +
    (loadsCountingByStatus?.received ?? 0) +
    (loadsCountingByStatus?.reconciled ?? 0);

  const handleStatusChange = React.useCallback(
    (v: Set<string | number>) => {
      const value = Array.from(v.values())[0] as string;
      setStatusFilter(value === 'all' ? undefined : (value as never));
      setOffset(0);
    },
    [setStatusFilter, setOffset],
  );

  const handleSearchChange = React.useCallback(
    (value: string | undefined) => {
      setSearch(value ?? '');
      setOffset(0);
    },
    [setSearch, setOffset],
  );

  return (
    <>
      <ToggleButtonGroup
        className="@LoadListFilterBar"
        selectionMode="single"
        selectedKeys={statusFilter ? [statusFilter] : ['all']}
        onSelectionChange={handleStatusChange}
      >
        <LoadStatusToggle id="all" label="All" count={allCount} />
        <LoadStatusToggle
          id="Shipped"
          label="Shipped"
          count={loadsCountingByStatus?.shipped}
          disabled={!loadsCountingByStatus?.shipped}
        />
        <LoadStatusToggle
          id="Received"
          label="Received"
          count={loadsCountingByStatus?.received}
          disabled={!loadsCountingByStatus?.received}
        />
        <LoadStatusToggle
          id="Reconciled"
          label="Reconciled"
          count={loadsCountingByStatus?.reconciled}
          disabled={!loadsCountingByStatus?.reconciled}
        />
      </ToggleButtonGroup>
      <SearchField
        autoFocus
        placeholder="Search loads"
        aria-label="Search loads"
        value={search}
        className="h-[34px] w-[250px] [&>div]:h-full"
        onChange={handleSearchChange}
      />
    </>
  );
};
